const { jQuery: $ } = window;

import FilterControl from "./FilterControl";

/**
 * Base adapter for a filter rendered as a GROUP of same-named native inputs (checkboxes or radios),
 * rather than a single control. Reads/writes across the whole group and, via the `multiple` flag,
 * returns either an array of checked values (checkboxes) or a single scalar (radios).
 *
 * Because it's backed by native inputs, it inherits keyboard accessibility and native form-reset for free;
 * the change handler, clear, and history restore all work through the standard adapter interface.
 */
export default class GroupFilter extends FilterControl {
    /**
     * Whether more than one value can be selected. Subclasses override (checkboxes → true, radios → false).
     * @returns {boolean}
     */
    get multiple() {
        return false;
    }

    /**
     * All grouped inputs for this filter.
     * @returns {jQuery}
     */
    controls() {
        return this.$el.find("input[name]");
    }

    /**
     * @returns {string|string[]} Checked value(s) — an array when `multiple`, otherwise a scalar (or "").
     */
    getValue() {
        const checked = this.controls().filter(":checked");

        if(this.multiple) {
            return checked.map((i, el) => el.value).get();
        }

        return checked.length ? checked.first().val() : "";
    }

    /**
     * Checks the inputs whose value is in the given set; unchecks the rest. Does not fire change.
     * @param {string|string[]|null} value
     */
    setValue(value) {
        const values = (value === null || value === undefined)
            ? []
            : (Array.isArray(value) ? value.map(String) : [String(value)]);
        const wanted = new Set(values);

        this.controls().each((i, el) => {
            el.checked = wanted.has(String(el.value));
        });
    }

    /**
     * Unchecks every input in the group.
     */
    reset() {
        this.controls().each((i, el) => {
            el.checked = false;
        });
    }

    /**
     * @param {Function} handler
     */
    bindChange(handler) {
        this.controls().on("change", handler);
    }

    /**
     * Triggers change once (on the first input) so the bound handler runs.
     */
    fireChange() {
        this.controls().first().trigger("change");
    }

    /**
     * Active selections as `[{ value, label }]` for the applied-filter chips — one per checked input. Reads
     * the label from the input's sibling span, preferring the original text stashed in `data-kpfLabel` by
     * applyFilterData (so the "(N)" count suffix never leaks into a chip). The empty/"All" input is skipped.
     *
     * @returns {{value: string, label: string}[]}
     */
    getSelectionLabels() {
        const out = [];
        this.controls().filter(":checked").each((i, el) => {
            if(el.value === "") {
                return;
            }
            const $span = $(el).siblings("span").first();
            const stashed = $span.data("kpfLabel");
            const label = (stashed !== undefined ? String(stashed) : ($span.text() || "").trim()) || el.value;
            out.push({ value: el.value, label });
        });
        return out;
    }

    /**
     * Applies batch filter-data. Grouped controls are rendered server-side (already narrowed to the active
     * post types), so `options` is ignored — only `counts` is used: appends " (N)" to each option's label
     * and disables zero-count options. `data.counts` is `{ optionValue: count }`; options missing from it
     * default to 0. The empty/"All" option is skipped. Native inputs, so changes apply immediately.
     *
     * @param {object} data { counts?: {value=>count} }
     */
    applyFilterData(data) {
        // React only to a counts payload. An options-only payload (e.g. a taxonomy narrowing request fired
        // when counts are disabled) has no `counts` key — leave the group's labels/enabled state untouched
        // rather than relabelling everything "(0)" and disabling it. An empty (but present) map is honored:
        // it means every option is genuinely zero for the current query.
        if(!data || data.counts === undefined || data.counts === null) {
            return;
        }

        const counts = data.counts;

        this.controls().each((i, el) => {
            const $input = $(el);
            const value = String(el.value);

            if(value === "") {
                return; // the "All"/reset option gets no count
            }

            const $label = $input.siblings("span").first();
            if(!$label.length) {
                return;
            }

            // Preserve the original label once so repeated applies don't stack suffixes.
            if($label.data("kpfLabel") === undefined) {
                $label.data("kpfLabel", $label.text().trim());
            }

            const count = counts[value] ?? 0;
            $label.text(`${$label.data("kpfLabel")} (${count})`);
            $input.prop("disabled", count === 0);
            $input.closest("label").toggleClass("kpfsc--disabled", count === 0);
        });
    }
}
